import { apiGet, apiPost, apiPatch, apiDelete } from './api';
import type { TimeEntry, HalCollection, TimeEntryFormData } from '@/types';

// Obținere înregistrări de timp pentru un work package
export async function getWorkPackageTimeEntries(
  workPackageId: number
): Promise<TimeEntry[]> {
  // Folosim direct endpoint-ul auth cu parametri simpli
  const response = await apiGet<HalCollection<TimeEntry>>(
    '/auth/data/time-entries',
    {
      params: {
        workPackageId: workPackageId,
      },
    }
  );

  return response._embedded.elements;
}

// Obținere înregistrări de timp pentru un proiect
export async function getProjectTimeEntries(
  projectId: number
): Promise<TimeEntry[]> {
  const response = await apiGet<HalCollection<TimeEntry>>(
    '/time_entries',
    {
      params: {
        filters: JSON.stringify([
          { project: { operator: '=', values: [String(projectId)] } },
        ]),
        pageSize: 100,
      },
    }
  );

  return response._embedded.elements;
}

// Obținere înregistrările mele de timp
export async function getMyTimeEntries(filters?: {
  projectId?: number;
  workPackageId?: number;
  from?: string;
  to?: string;
}): Promise<TimeEntry[]> {
  const filterArray: { [key: string]: { operator: string; values: string[] } }[] = [
    { user: { operator: '=', values: ['me'] } },
  ];

  if (filters?.projectId) {
    filterArray.push({
      project: { operator: '=', values: [String(filters.projectId)] },
    });
  }

  if (filters?.workPackageId) {
    filterArray.push({
      work_package: { operator: '=', values: [String(filters.workPackageId)] },
    });
  }

  if (filters?.from && filters?.to) {
    filterArray.push({
      spent_on: { operator: '<>d', values: [filters.from, filters.to] },
    });
  }

  const response = await apiGet<HalCollection<TimeEntry>>(
    '/time_entries',
    {
      params: {
        filters: JSON.stringify(filterArray),
        pageSize: 100,
      },
    }
  );

  return response._embedded.elements;
}

// Creare înregistrare de timp
export async function createTimeEntry(data: TimeEntryFormData): Promise<TimeEntry> {
  const payload: Record<string, unknown> = {
    hours: `PT${data.hours}H`,
    spentOn: data.spentOn,
    _links: {
      project: { href: `/api/v3/projects/${data.projectId}` },
      workPackage: { href: `/api/v3/work_packages/${data.workPackageId}` },
      activity: { href: `/api/v3/time_entries/activities/${data.activityId}` },
    },
  };

  if (data.comment) {
    payload.comment = { raw: data.comment };
  }

  return apiPost<TimeEntry>('/time_entries', payload);
}

// Actualizare înregistrare de timp
export async function updateTimeEntry(
  timeEntryId: number,
  data: Partial<TimeEntryFormData>
): Promise<TimeEntry> {
  const payload: Record<string, unknown> = {};

  if (data.hours !== undefined) {
    payload.hours = `PT${data.hours}H`;
  }

  if (data.spentOn) {
    payload.spentOn = data.spentOn;
  }

  if (data.comment !== undefined) {
    payload.comment = data.comment ? { raw: data.comment } : null;
  }

  const links: Record<string, unknown> = {};

  if (data.activityId) {
    links.activity = { href: `/api/v3/time_entries/activities/${data.activityId}` };
  }

  if (Object.keys(links).length > 0) {
    payload._links = links;
  }

  return apiPatch<TimeEntry>(`/time_entries/${timeEntryId}`, payload);
}

// Ștergere înregistrare de timp
export async function deleteTimeEntry(timeEntryId: number): Promise<void> {
  await apiDelete(`/time_entries/${timeEntryId}`);
}

// Obținere activități disponibile pentru time entries
export interface TimeEntryActivity {
  id: number;
  name: string;
  position: number;
  isDefault: boolean;
  _links: {
    self: { href: string };
  };
}

export async function getTimeEntryActivities(): Promise<TimeEntryActivity[]> {
  const response = await apiGet<HalCollection<TimeEntryActivity>>(
    '/time_entries/activities'
  );

  return response._embedded.elements;
}

// Calcul timp total din lista de time entries
export function calculateTotalTime(entries: TimeEntry[]): {
  hours: number;
  formatted: string;
} {
  let totalMinutes = 0;

  entries.forEach((entry) => {
    // Parse ISO 8601 duration (PT1H30M)
    const hours = parseISODuration(entry.hours);
    totalMinutes += hours * 60;
  });

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  let formatted = '';
  if (hours > 0) {
    formatted += `${hours}h`;
  }
  if (minutes > 0) {
    formatted += ` ${minutes}m`;
  }
  if (!formatted) {
    formatted = '0h';
  }

  return {
    hours: totalMinutes / 60,
    formatted: formatted.trim(),
  };
}

// Parse ISO 8601 duration în ore
export function parseISODuration(duration: string | number | null | undefined): number {
  if (duration === null || duration === undefined) return 0;

  // Dacă e deja un număr, îl returnăm direct
  if (typeof duration === 'number') return duration;

  // Dacă nu e string, încercăm să-l convertim
  if (typeof duration !== 'string') {
    const num = Number(duration);
    return isNaN(num) ? 0 : num;
  }

  // Dacă e un string numeric simplu (ex: "4.5")
  const numericValue = parseFloat(duration);
  if (!isNaN(numericValue) && !duration.includes('PT')) {
    return numericValue;
  }

  // Format: PT1H30M sau PT1.5H
  const match = duration.match(/PT(?:(\d+(?:\.\d+)?)H)?(?:(\d+)M)?/);

  if (!match) return 0;

  const hours = parseFloat(match[1] || '0');
  const minutes = parseInt(match[2] || '0', 10);

  return hours + minutes / 60;
}

// Format ore în ISO 8601 duration
export function formatHoursToISO(hours: number): string {
  const wholeHours = Math.floor(hours);
  const minutes = Math.round((hours - wholeHours) * 60);

  if (minutes > 0) {
    return `PT${wholeHours}H${minutes}M`;
  }

  return `PT${wholeHours}H`;
}

// Format ore pentru afișare
export function formatHoursForDisplay(hours: number): string {
  const wholeHours = Math.floor(hours);
  const minutes = Math.round((hours - wholeHours) * 60);

  if (minutes > 0) {
    return `${wholeHours}h ${minutes}m`;
  }

  return `${wholeHours}h`;
}
